import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatDate, formatEUR, DOSSIER_STATUSES, FINANCEMENT_STATUSES, DENONCIATION_STATUSES, OPTION_STATUSES, FACTURE_STATUSES } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, TextField, SelectField, TextArea, Section } from "@/components/ui-kit";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Save, Mail, CheckSquare, Square, History as HistoryIcon, MessageSquare, Sparkles, Copy, X, Send, Upload, Download, Trash2, FileText, Paperclip } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function DossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [tab, setTab] = useState("comments");
  const [emailModal, setEmailModal] = useState(null); // { slug }

  const canEdit = ["admin", "dirigeant", "secretaire_bo", "responsable_facturation"].includes(user?.role);

  const load = () => {
    api.get(`/dossiers/${id}`).then((r) => setDossier(r.data)).catch(() => navigate("/dossiers"));
    api.get(`/dossiers/${id}/comments`).then((r) => setComments(r.data));
    api.get(`/history/dossier/${id}`).then((r) => setHistory(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!dossier) return <div className="p-8">Chargement…</div>;

  const set = (k, v) => setDossier((d) => ({ ...d, [k]: v }));

  const save = async () => {
    try {
      const payload = {
        statut: dossier.statut, financement_statut: dossier.financement_statut,
        financement_banque: dossier.financement_banque, financement_montant: dossier.financement_montant ? parseFloat(dossier.financement_montant) : null,
        financement_date_accord: dossier.financement_date_accord, financement_relance_niveau: dossier.financement_relance_niveau,
        date_notaire: dossier.date_notaire, notaire_nom: dossier.notaire_nom, notaire_adresse: dossier.notaire_adresse,
        denonciation_statut: dossier.denonciation_statut, denonciation_date: dossier.denonciation_date,
        option_statut: dossier.option_statut, option_date: dossier.option_date,
        facture_statut: dossier.facture_statut, facture_numero: dossier.facture_numero,
        facture_montant: dossier.facture_montant ? parseFloat(dossier.facture_montant) : null,
        facture_date_envoi: dossier.facture_date_envoi, facture_date_paiement: dossier.facture_date_paiement,
        commission_montant: dossier.commission_montant ? parseFloat(dossier.commission_montant) : null,
        commission_vendeur_taux: dossier.commission_vendeur_taux === "" || dossier.commission_vendeur_taux === undefined || dossier.commission_vendeur_taux === null ? null : parseFloat(dossier.commission_vendeur_taux),
        annule: !!dossier.annule,
        client_code_postal: dossier.client_code_postal,
        a_co_acquereur: !!dossier.a_co_acquereur,
        co_prenom: dossier.co_prenom, co_nom: dossier.co_nom, co_email: dossier.co_email,
        co_telephone: dossier.co_telephone, co_adresse: dossier.co_adresse,
        co_ville: dossier.co_ville, co_code_postal: dossier.co_code_postal,
      };
      await api.put(`/dossiers/${id}`, payload);
      toast.success("Dossier mis à jour");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleTask = async (idx) => {
    if (!canEdit) return;
    await api.post(`/dossiers/${id}/tasks/${idx}/toggle`);
    load();
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    await api.post(`/dossiers/${id}/comments`, { contenu: newComment, type: "admin" });
    setNewComment("");
    load();
  };

  const openEmail = (slug) => setEmailModal({ slug });

  return (
    <div className="p-8" data-testid="dossier-detail-page">
      <Toaster position="top-right" richColors />
      <Link to="/dossiers" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#111111] mb-4" data-testid="back-to-dossiers">
        <ArrowLeft className="w-4 h-4" /> Retour aux dossiers
      </Link>

      <PageHeader
        title={`${dossier.client_prenom} ${dossier.client_nom}`}
        subtitle={`Dossier · ${dossier.programme || "Programme"} · Lot ${dossier.lot || "—"}`}
        actions={
          <>
            <StatusBadge value={dossier.statut} kind="dossier" />
            <SecondaryButton
              onClick={() => window.dispatchEvent(new CustomEvent("open-assistant", { detail: { dossier_id: id, label: `Dossier ${dossier.client_prenom} ${dossier.client_nom}` } }))}
              data-testid="ask-ai-dossier-btn"
            >
              <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#B8873A]" /> Demander à l&apos;IA</span>
            </SecondaryButton>
            {canEdit && <PrimaryButton onClick={save} data-testid="save-dossier-button"><span className="flex items-center gap-2"><Save className="w-4 h-4" /> Enregistrer</span></PrimaryButton>}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Récapitulatif dossier">
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Info label="Prix" value={formatEUR(dossier.prix)} />
              <ClickableInfo label="Commission" value={formatEUR(dossier.commission_montant)} target="section-facturation" />
              <Info label="Signé le" value={formatDate(dossier.date_signature)} />
              <Info label="Promoteur" value={dossier.promoteur || "—"} />
              <Info label="Email" value={dossier.client_email || "—"} />
              <Info label="Téléphone" value={dossier.client_telephone || "—"} />
              <Info label="Ville" value={[dossier.client_code_postal, dossier.client_ville].filter(Boolean).join(" ") || "—"} />
              <Info label="Statut global" value={<StatusBadge value={dossier.statut} kind="dossier" />} />
              <ClickableInfo label="Dénonciation" value={<StatusBadge value={dossier.denonciation_statut} kind="denonciation" />} target="section-denonc" />
              <ClickableInfo label="Option" value={<StatusBadge value={dossier.option_statut} kind="option" />} target="section-denonc" />
              <ClickableInfo label="Financement" value={<StatusBadge value={dossier.financement_statut} kind="financement" />} target="section-financement" />
              <ClickableInfo label="Facture" value={<StatusBadge value={dossier.facture_statut} kind="facture" />} target="section-facturation" />
            </div>
          </Section>

          <div id="section-denonc">
          <Section title="Dénonciation & Option">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Statut dénonciation" value={dossier.denonciation_statut || "a_traiter"} onChange={(e) => set("denonciation_statut", e.target.value)} options={DENONCIATION_STATUSES} disabled={!canEdit} testid="detail-denonc-statut" />
              <TextField label="Date dénonciation" type="date" value={(dossier.denonciation_date || "").slice(0,10)} onChange={(e) => set("denonciation_date", e.target.value)} disabled={!canEdit} testid="detail-denonc-date" />
              <SelectField label="Statut option" value={dossier.option_statut || "a_traiter"} onChange={(e) => set("option_statut", e.target.value)} options={OPTION_STATUSES} disabled={!canEdit} testid="detail-option-statut" />
              <TextField label="Date option" type="date" value={(dossier.option_date || "").slice(0,10)} onChange={(e) => set("option_date", e.target.value)} disabled={!canEdit} testid="detail-option-date" />
              <div className="md:col-span-2 flex gap-2">
                <SecondaryButton onClick={() => openEmail("denonciation_promoteur")} data-testid="email-denonc-btn"><span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email dénonciation</span></SecondaryButton>
                <SecondaryButton onClick={() => openEmail("option_promoteur")} data-testid="email-option-btn"><span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email option</span></SecondaryButton>
                <SecondaryButton onClick={() => openEmail("relance_promoteur")}><span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Relance promoteur</span></SecondaryButton>
              </div>
            </div>
          </Section>
          </div>

          <div id="section-co-acquereur">
          <Section title="Co-acquéreur" actions={
            <label className="flex items-center gap-2 cursor-pointer select-none text-xs">
              <input type="checkbox" checked={!!dossier.a_co_acquereur} onChange={(e) => set("a_co_acquereur", e.target.checked)} disabled={!canEdit} className="w-4 h-4 accent-[#B8873A]" data-testid="dossier-co-acquereur-checkbox" />
              <span className="text-slate-600">Présence d&apos;un co-acquéreur</span>
            </label>
          }>
            {dossier.a_co_acquereur ? (
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dossier-co-acquereur-section">
                <TextField label="Prénom" value={dossier.co_prenom || ""} onChange={(e) => set("co_prenom", e.target.value)} disabled={!canEdit} testid="dossier-co-prenom" />
                <TextField label="Nom" value={dossier.co_nom || ""} onChange={(e) => set("co_nom", e.target.value)} disabled={!canEdit} testid="dossier-co-nom" />
                <TextField label="Email" value={dossier.co_email || ""} onChange={(e) => set("co_email", e.target.value)} disabled={!canEdit} testid="dossier-co-email" />
                <TextField label="Téléphone" value={dossier.co_telephone || ""} onChange={(e) => set("co_telephone", e.target.value)} disabled={!canEdit} testid="dossier-co-telephone" />
                <div className="md:col-span-2">
                  <TextField label="Adresse (si différente)" value={dossier.co_adresse || ""} onChange={(e) => set("co_adresse", e.target.value)} disabled={!canEdit} testid="dossier-co-adresse" />
                </div>
                <TextField label="Ville" value={dossier.co_ville || ""} onChange={(e) => set("co_ville", e.target.value)} disabled={!canEdit} testid="dossier-co-ville" />
                <TextField label="Code postal" value={dossier.co_code_postal || ""} onChange={(e) => set("co_code_postal", e.target.value)} disabled={!canEdit} testid="dossier-co-code-postal" />
              </div>
            ) : (
              <div className="p-5 text-sm text-slate-400" data-testid="dossier-co-acquereur-empty">Aucun co-acquéreur sur ce dossier. Coche la case ci-dessus pour en ajouter un.</div>
            )}
          </Section>
          </div>

          <div id="section-financement">
          <Section title="Financement">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Statut financement" value={dossier.financement_statut || "en_attente"} onChange={(e) => set("financement_statut", e.target.value)} options={FINANCEMENT_STATUSES} disabled={!canEdit} testid="detail-fin-statut" />
              <TextField label="Banque" value={dossier.financement_banque || ""} onChange={(e) => set("financement_banque", e.target.value)} disabled={!canEdit} testid="detail-fin-banque" />
              <TextField label="Montant financé (€)" type="number" value={dossier.financement_montant || ""} onChange={(e) => set("financement_montant", e.target.value)} disabled={!canEdit} testid="detail-fin-montant" />
              <TextField label="Date accord" type="date" value={(dossier.financement_date_accord || "").slice(0,10)} onChange={(e) => set("financement_date_accord", e.target.value)} disabled={!canEdit} testid="detail-fin-accord" />
              <SelectField label="Niveau relance" value={String(dossier.financement_relance_niveau ?? 0)} onChange={(e) => set("financement_relance_niveau", parseInt(e.target.value))}
                options={[{value:"0",label:"Aucune"},{value:"1",label:"Niveau 1 - Courtoise"},{value:"2",label:"Niveau 2 - Ferme"},{value:"3",label:"Niveau 3 - Urgent"}]} disabled={!canEdit} testid="detail-fin-relance" />
              <div className="md:col-span-2 flex flex-wrap gap-2">
                <SecondaryButton onClick={() => openEmail("relance_financement_1")} data-testid="email-fin1-btn">Relance financement N1</SecondaryButton>
                <SecondaryButton onClick={() => openEmail("relance_financement_2")}>Relance financement N2</SecondaryButton>
                <SecondaryButton onClick={() => openEmail("relance_financement_3")}>Relance financement N3</SecondaryButton>
              </div>
            </div>
          </Section>
          </div>

          <div id="section-notaire">
          <Section title="Passage notaire">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Date passage notaire" type="date" value={(dossier.date_notaire || "").slice(0,10)} onChange={(e) => set("date_notaire", e.target.value)} disabled={!canEdit} testid="detail-notaire-date" />
              <TextField label="Nom notaire" value={dossier.notaire_nom || ""} onChange={(e) => set("notaire_nom", e.target.value)} disabled={!canEdit} testid="detail-notaire-nom" />
              <div className="md:col-span-2">
                <TextField label="Adresse notaire" value={dossier.notaire_adresse || ""} onChange={(e) => set("notaire_adresse", e.target.value)} disabled={!canEdit} testid="detail-notaire-adresse" />
              </div>
            </div>
          </Section>
          </div>

          <div id="section-facturation">
          <Section title="Facturation & Recouvrement">
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Statut facture" value={dossier.facture_statut || "a_emettre"} onChange={(e) => set("facture_statut", e.target.value)} options={FACTURE_STATUSES} disabled={!canEdit} testid="detail-fact-statut" />
              <TextField label="N° facture" value={dossier.facture_numero || ""} onChange={(e) => set("facture_numero", e.target.value)} disabled={!canEdit} testid="detail-fact-numero" />
              <TextField label="Montant facture (€)" type="number" value={dossier.facture_montant || ""} onChange={(e) => set("facture_montant", e.target.value)} disabled={!canEdit} testid="detail-fact-montant" />
              <TextField label="Commission agence (€)" type="number" value={dossier.commission_montant || ""} onChange={(e) => set("commission_montant", e.target.value)} disabled={!canEdit} testid="detail-fact-commission" />
              <TextField label="Taux commission vendeur (%)" type="number" step="0.1" value={dossier.commission_vendeur_taux ?? ""} onChange={(e) => set("commission_vendeur_taux", e.target.value)} disabled={!canEdit} placeholder="ex: 1.5" testid="detail-commission-vendeur-taux" />
              <div className="md:col-span-2 grid grid-cols-2 gap-4 bg-slate-50 border border-slate-200 rounded-sm p-3">
                <Info label="Commission vendeur" value={formatEUR((dossier.prix || 0) * (parseFloat(dossier.commission_vendeur_taux) || 0) / 100)} />
                <Info label="Marge nette agence" value={formatEUR((dossier.commission_montant || 0) - (dossier.prix || 0) * (parseFloat(dossier.commission_vendeur_taux) || 0) / 100)} />
              </div>
              <div className="md:col-span-2 flex items-center justify-between border border-red-200 bg-red-50 rounded-sm p-3">
                <div>
                  <div className="text-sm font-semibold text-red-800">Vente annulée</div>
                  <div className="text-xs text-red-600">Exclut le dossier du CA dans Analytics</div>
                </div>
                <button type="button" disabled={!canEdit} onClick={() => set("annule", !dossier.annule)} data-testid="toggle-annule" className={`relative w-14 h-7 rounded-full transition-colors ${dossier.annule ? "bg-red-600" : "bg-slate-300"} disabled:opacity-50`}>
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${dossier.annule ? "translate-x-7" : ""}`} />
                </button>
              </div>
              <TextField label="Date envoi" type="date" value={(dossier.facture_date_envoi || "").slice(0,10)} onChange={(e) => set("facture_date_envoi", e.target.value)} disabled={!canEdit} testid="detail-fact-envoi" />
              <TextField label="Date paiement" type="date" value={(dossier.facture_date_paiement || "").slice(0,10)} onChange={(e) => set("facture_date_paiement", e.target.value)} disabled={!canEdit} testid="detail-fact-paiement" />
              <div className="md:col-span-2 flex flex-wrap gap-2">
                <SecondaryButton onClick={() => openEmail("envoi_facture_promoteur")} data-testid="email-facture-btn">Envoi facture promoteur</SecondaryButton>
                <SecondaryButton onClick={() => openEmail("relance_paiement_promoteur")}>Relance paiement</SecondaryButton>
                <SecondaryButton onClick={() => openEmail("confirmation_cloture")}>Confirmation clôture</SecondaryButton>
                <SecondaryButton onClick={() => openEmail("remerciement_signature")} data-testid="email-remerciement-btn">Email remerciement client</SecondaryButton>
              </div>
            </div>
          </Section>
          </div>
        </div>

        <div className="space-y-4">
          <Section title="Tâches automatiques">
            <div className="p-4 space-y-2">
              {(dossier.tasks || []).map((t, i) => (
                <button key={i} onClick={() => toggleTask(i)} disabled={!canEdit}
                  className={`w-full flex items-start gap-3 p-2 rounded-sm text-left transition-colors ${t.done ? "bg-emerald-50" : "hover:bg-slate-50"} ${!canEdit ? "cursor-default" : ""}`}
                  data-testid={`task-${i}`}>
                  {t.done ? <CheckSquare className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <Square className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <div className={`text-sm ${t.done ? "line-through text-slate-500" : "text-slate-800"}`}>{t.label}</div>
                    {t.done && t.done_by && <div className="text-[10px] text-slate-500">{t.done_by} · {new Date(t.done_at).toLocaleDateString("fr-FR")}</div>}
                  </div>
                </button>
              ))}
            </div>
          </Section>

          <DocumentsPanel dossierId={id} canEdit={canEdit} canDelete={["admin", "dirigeant", "secretaire_bo"].includes(user?.role)} />

          <div className="bg-white rounded-sm border border-slate-200">
            <div className="flex border-b border-slate-200">
              <button data-testid="tab-comments" onClick={() => setTab("comments")} className={`flex-1 px-3 py-3 text-xs font-semibold uppercase tracking-wider ${tab === "comments" ? "text-[#111111] border-b-2 border-[#B8873A]" : "text-slate-500"}`}>
                <MessageSquare className="w-3 h-3 inline mr-1" /> Commentaires
              </button>
              <button data-testid="tab-history" onClick={() => setTab("history")} className={`flex-1 px-3 py-3 text-xs font-semibold uppercase tracking-wider ${tab === "history" ? "text-[#111111] border-b-2 border-[#B8873A]" : "text-slate-500"}`}>
                <HistoryIcon className="w-3 h-3 inline mr-1" /> Historique
              </button>
            </div>
            <div className="p-4 max-h-[500px] overflow-y-auto">
              {tab === "comments" && (
                <div>
                  <form onSubmit={postComment} className="mb-4">
                    <TextArea rows={3} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Commentaire administratif…" testid="new-dossier-comment" />
                    <PrimaryButton type="submit" className="mt-2 w-full">Ajouter</PrimaryButton>
                  </form>
                  {comments.length === 0 ? <div className="text-sm text-slate-500 text-center py-4">Aucun commentaire</div> :
                    comments.map((c) => (
                      <div key={c.id} className="border border-slate-200 rounded-sm p-3 bg-slate-50 mb-2">
                        <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.contenu}</div>
                        <div className="text-[11px] text-slate-500 mt-1">{c.user_name} · {new Date(c.created_at).toLocaleString("fr-FR")}</div>
                      </div>
                    ))}
                </div>
              )}
              {tab === "history" && (
                history.length === 0 ? <div className="text-sm text-slate-500 text-center py-4">Aucune activité</div> :
                <div>{history.map((h) => (
                  <div key={h.id} className="timeline-item">
                    <div className="text-sm font-semibold text-[#111111]">{h.action}</div>
                    {h.details && <div className="text-xs text-slate-600 mt-1">{h.details}</div>}
                    <div className="text-[11px] text-slate-500 mt-1">{h.user_name} · {new Date(h.created_at).toLocaleString("fr-FR")}</div>
                  </div>))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {emailModal && <EmailDraftModal dossier={dossier} slug={emailModal.slug} templates={templates} onClose={() => setEmailModal(null)} onSent={load} />}
    </div>
  );
}

function DocumentsPanel({ dossierId, canEdit, canDelete }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const load = () => api.get(`/dossiers/${dossierId}/documents`).then((r) => setDocs(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dossierId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/dossiers/${dossierId}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document téléversé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/dossiers/${dossierId}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Supprimer "${d.original_filename}" ?`)) return;
    await api.delete(`/dossiers/${dossierId}/documents/${d.id}`);
    toast.success("Document supprimé");
    load();
  };

  return (
    <Section title={`Documents (${docs.length})`} actions={canEdit && (
      <>
        <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="documents-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="text-xs font-semibold uppercase tracking-wider text-[#111111] hover:text-[#B8873A] flex items-center gap-1"
          data-testid="documents-upload-button">
          <Upload className="w-3 h-3" /> {uploading ? "Envoi…" : "Ajouter"}
        </button>
      </>
    )}>
      <div className="p-3">
        {docs.length === 0 ? (
          <div className="text-xs text-slate-500 text-center py-3">Aucun document</div>
        ) : docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 py-2 border-b border-slate-100 last:border-b-0" data-testid={`document-${d.id}`}>
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <button onClick={() => download(d)} className="flex-1 text-left text-sm text-[#111111] hover:text-[#B8873A] truncate">
              {d.original_filename}
            </button>
            <div className="text-[10px] text-slate-400">{Math.round((d.size || 0)/1024)} Ko</div>
            <button onClick={() => download(d)} className="text-slate-500 hover:text-[#111111]" title="Télécharger"><Download className="w-3.5 h-3.5" /></button>
            {canDelete && <button onClick={() => remove(d)} className="text-red-500 hover:text-red-700" title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
      </div>
    </Section>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 font-medium mt-1">{value}</div>
    </div>
  );
}

function ClickableInfo({ label, value, target }) {
  const scrollTo = () => {
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button onClick={scrollTo} className="text-left hover:opacity-80 transition-opacity cursor-pointer group" data-testid={`clickable-${target}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold group-hover:text-[#B8873A]">
        {label} <span className="text-[8px]">↓</span>
      </div>
      <div className="text-sm text-slate-800 font-medium mt-1">{value}</div>
    </button>
  );
}

function EmailDraftModal({ dossier, slug, templates, onClose, onSent }) {
  const dossierId = dossier?.id;
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [context, setContext] = useState("");
  const template = templates.find((t) => t.slug === slug);
  const [recipient, setRecipient] = useState("");

  const generate = async (ai = false) => {
    if (ai) setAiLoading(true); else setLoading(true);
    try {
      const { data } = await api.post("/emails/draft", { dossier_id: dossierId, template_slug: slug, ai_enhance: ai, custom_context: context });
      setDraft(data);
      // Auto-fill recipient from backend suggestion (routed to correct promoteur email based on template)
      if (data.recipient_suggestion && !recipient) setRecipient(data.recipient_suggestion);
      if (ai && data.ai_used) toast.success("Brouillon amélioré par IA");
      else if (ai && !data.ai_used) toast.error("Amélioration IA indisponible");
    } catch (e) { toast.error("Erreur"); }
    finally { setLoading(false); setAiLoading(false); }
  };

  useEffect(() => { generate(false); /* eslint-disable-next-line */ }, [slug]);

  const copyAll = () => {
    if (!draft) return;
    navigator.clipboard.writeText(`Sujet: ${draft.sujet}\n\n${draft.corps}`);
    toast.success("Copié dans le presse-papier");
  };

  const send = async () => {
    if (!recipient || !recipient.includes("@")) { toast.error("Destinataire invalide"); return; }
    if (!draft) return;
    setSending(true);
    try {
      await api.post("/emails/send", {
        dossier_id: dossierId,
        template_slug: slug,
        recipient_email: recipient,
        sujet: draft.sujet,
        corps: draft.corps,
      });
      toast.success(`Email envoyé à ${recipient}`);
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi échoué");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="email-draft-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Brouillon email</div>
            <h2 className="text-lg font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>{template?.nom || slug}</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800" data-testid="close-email-modal"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? <div className="text-center py-10 text-slate-500">Génération…</div> : draft && (
            <>
              <TextField label="Destinataire" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="email@destinataire.fr" testid="draft-recipient" />
              <TextField label="Sujet" value={draft.sujet} onChange={(e) => setDraft({ ...draft, sujet: e.target.value })} testid="draft-sujet" />
              <TextArea label="Corps" rows={12} value={draft.corps} onChange={(e) => setDraft({ ...draft, corps: e.target.value })} testid="draft-corps" />
              <TextArea label="Contexte additionnel pour l'IA (optionnel)" rows={2} value={context} onChange={(e) => setContext(e.target.value)} placeholder="Ex: mentionner qu'un premier RDV a eu lieu la semaine dernière…" testid="draft-context" />
              <div className="flex flex-wrap gap-2 justify-end">
                <SecondaryButton onClick={copyAll} data-testid="copy-email-btn"><span className="flex items-center gap-1"><Copy className="w-4 h-4" /> Copier</span></SecondaryButton>
                <SecondaryButton onClick={() => generate(true)} disabled={aiLoading} data-testid="enhance-ai-btn"><span className="flex items-center gap-1"><Sparkles className="w-4 h-4 text-[#B8873A]" /> {aiLoading ? "Amélioration…" : "Améliorer avec IA"}</span></SecondaryButton>
                <PrimaryButton onClick={send} disabled={sending || !recipient} data-testid="send-email-btn">
                  <span className="flex items-center gap-1"><Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer"}</span>
                </PrimaryButton>
              </div>
              {draft.ai_used && (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm">
                  Ce brouillon a été amélioré par IA. Relisez avant envoi.
                </div>
              )}
              <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                Astuce : si l&apos;envoi affiche « désactivé », ajoutez votre clé API Resend dans <code className="bg-slate-100 px-1">/app/backend/.env</code> (variable <code className="bg-slate-100 px-1">RESEND_API_KEY</code>) puis redémarrez le backend.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
